<script setup lang="ts">
import {
  computed,
  onBeforeUnmount,
  onMounted,
  ref,
  shallowRef,
  watch,
} from 'vue'

import { publicApi } from '../api'
import { useClicker } from '../clicker'
import AppMark from '../components/AppMark.vue'
import LoadingState from '../components/LoadingState.vue'
import SlidePreview from '../components/SlidePreview.vue'
import { preloadSlides } from '../media'
import type { PublicPresentation } from '../types'

const props = defineProps<{
  slug?: string | undefined
}>()

interface StoredController {
  token: string
  savedAt: number
}

const deck = ref<PublicPresentation | null>(null)
const loading = ref(true)
const error = ref('')
const code = ref('')
const timerSeconds = ref(0)
const timerRunning = ref(true)
const startedAt = ref(Date.now())
const accumulated = ref(0)
const speakerNotes = ref<string[]>([])
const controllerToken = ref('')
const clicker = shallowRef<ReturnType<typeof useClicker> | null>(null)
let timerInterval = 0
let touchStartX = 0
let syncingRevision = 0

const current = computed(() => {
  const index = clicker.value?.state.value.slideIndex ?? 0
  return Math.max(0, Math.min(Math.max(0, (deck.value?.doc.slides.length ?? 1) - 1), index))
})
const currentSlide = computed(() => deck.value?.doc.slides[current.value] ?? null)
const nextSlide = computed(() => deck.value?.doc.slides[current.value + 1] ?? null)
const currentNotes = computed(() => speakerNotes.value[current.value] ?? '')
const connectionStatus = computed(() => clicker.value?.status.value ?? 'connecting')
const timerText = computed(() => {
  const minutes = Math.floor(timerSeconds.value / 60)
  const seconds = timerSeconds.value % 60
  return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`
})

function storageKey(slug: string): string {
  return `getslides:controller:${slug}`
}

function readStoredToken(slug: string): string {
  try {
    const raw = sessionStorage.getItem(storageKey(slug))
    if (!raw) return ''
    const saved = JSON.parse(raw) as StoredController
    if (!saved.token || Date.now() - saved.savedAt > 12 * 60 * 60 * 1_000) {
      sessionStorage.removeItem(storageKey(slug))
      return ''
    }
    return saved.token
  } catch {
    return ''
  }
}

function saveToken(slug: string, token: string): void {
  sessionStorage.setItem(storageKey(slug), JSON.stringify({
    token,
    savedAt: Date.now(),
  } satisfies StoredController))
}

async function connectWith(
  credential: { token: string } | { code: string },
): Promise<void> {
  const access = await publicApi.connectController(credential)
  saveToken(access.slug, access.controllerToken)
  controllerToken.value = access.controllerToken
  window.history.replaceState({}, '', `/p/${encodeURIComponent(access.slug)}/remote`)
  deck.value = await publicApi.get(access.slug)
  speakerNotes.value = access.notes
  void preloadSlides(deck.value.assets.slides)
  clicker.value?.close()
  clicker.value = useClicker({
    role: 'controller',
    token: access.controllerToken,
    initial: {
      revision: access.revision,
      slideIndex: access.slideIndex,
      seq: access.seq,
    },
  })
}

async function restoreWithToken(token: string): Promise<void> {
  if (!props.slug) throw new Error('Не удалось определить комнату.')
  const access = await publicApi.resumeController(token)
  if (access.slug !== props.slug) {
    throw new Error('Сессия пульта относится к другой презентации.')
  }
  controllerToken.value = token
  deck.value = await publicApi.get(access.slug)
  speakerNotes.value = access.notes
  void preloadSlides(deck.value.assets.slides)
  clicker.value = useClicker({
    role: 'controller',
    token,
    initial: {
      revision: access.revision,
      slideIndex: access.slideIndex,
      seq: access.seq,
    },
  })
}

async function syncDeckRevision(revision: number): Promise<void> {
  const token = controllerToken.value
  const slug = deck.value?.slug ?? props.slug
  if (
    !token
    || !slug
    || deck.value?.revision === revision
    || syncingRevision === revision
  ) return
  syncingRevision = revision
  try {
    const [access, fresh] = await Promise.all([
      publicApi.resumeController(token),
      publicApi.get(slug),
    ])
    if (access.slug !== slug) throw new Error('Сессия пульта относится к другой презентации.')
    speakerNotes.value = access.notes
    deck.value = fresh
    void preloadSlides(fresh.assets.slides)
    document.title = `Пульт — ${fresh.title}`
  } catch (cause) {
    console.warn('Could not refresh controller deck', cause)
  } finally {
    syncingRevision = 0
  }
}

async function load(): Promise<void> {
  loading.value = true
  error.value = ''
  let storedTokenUsed = ''
  try {
    const stored = props.slug ? readStoredToken(props.slug) : ''
    const oneTimeToken = new URLSearchParams(window.location.search).get('t')?.trim() ?? ''
    if (oneTimeToken) await connectWith({ token: oneTimeToken })
    else if (stored) {
      storedTokenUsed = stored
      await restoreWithToken(stored)
    }
    else throw new Error('Откройте QR-код ещё раз или введите код комнаты.')
    document.title = `Пульт — ${deck.value?.title ?? 'GetSlides'}`
  } catch (cause) {
    if (storedTokenUsed && props.slug) {
      sessionStorage.removeItem(storageKey(props.slug))
    }
    error.value = cause instanceof Error ? cause.message : 'Не удалось подключить пульт.'
  } finally {
    loading.value = false
  }
}

async function connectByCode(): Promise<void> {
  if (!/^\d{6}$/.test(code.value)) return
  loading.value = true
  error.value = ''
  try {
    await connectWith({ code: code.value })
  } catch (cause) {
    error.value = cause instanceof Error ? cause.message : 'Код не подошёл.'
  } finally {
    loading.value = false
  }
}

function go(to: number): void {
  if (!deck.value || !clicker.value) return
  const target = Math.max(0, Math.min(deck.value.doc.slides.length - 1, to))
  clicker.value.navigate(target)
}

function onTouchStart(event: TouchEvent): void {
  touchStartX = event.touches[0]?.clientX ?? 0
}

function onTouchEnd(event: TouchEvent): void {
  const x = event.changedTouches[0]?.clientX
  if (x === undefined || Math.abs(x - touchStartX) < 45) return
  go(current.value + (x < touchStartX ? 1 : -1))
}

function toggleTimer(): void {
  if (timerRunning.value) {
    accumulated.value = timerSeconds.value
    timerRunning.value = false
  } else {
    startedAt.value = Date.now()
    timerRunning.value = true
  }
}

function resetTimer(): void {
  accumulated.value = 0
  timerSeconds.value = 0
  startedAt.value = Date.now()
}

function tickTimer(): void {
  if (!timerRunning.value) return
  timerSeconds.value = accumulated.value + Math.floor((Date.now() - startedAt.value) / 1_000)
}

onMounted(() => {
  void load()
  timerInterval = window.setInterval(tickTimer, 500)
})

watch(
  () => clicker.value?.terminalError.value ?? '',
  (message) => {
    if (!message) return
    const slug = deck.value?.slug ?? props.slug
    if (slug) sessionStorage.removeItem(storageKey(slug))
    const active = clicker.value
    clicker.value = null
    active?.close()
    controllerToken.value = ''
    speakerNotes.value = []
    error.value = message
  },
)

watch(
  () => clicker.value?.state.value.revision ?? 0,
  (revision) => {
    if (revision > 0) void syncDeckRevision(revision)
  },
)

onBeforeUnmount(() => {
  clicker.value?.close()
  window.clearInterval(timerInterval)
})
</script>

<template>
  <main class="remote-page">
    <header class="remote-header">
      <span class="brand">
        <AppMark />
        <span>Пульт</span>
      </span>
      <div class="remote-connection" :class="`remote-connection--${connectionStatus}`">
        <span></span>
        {{ connectionStatus === 'connected' ? 'На связи' : connectionStatus === 'reconnecting' ? 'Возвращаем связь' : 'Подключаемся' }}
      </div>
    </header>

    <LoadingState v-if="loading" title="Подключаем пульт" full />

    <section v-else-if="error || !deck || !clicker" class="remote-login">
      <span class="remote-login__icon">⌁</span>
      <h1>Подключиться к показу</h1>
      <p>{{ error || 'Введите шестизначный код с большого экрана.' }}</p>
      <form @submit.prevent="connectByCode">
        <input
          v-model="code"
          type="text"
          inputmode="numeric"
          pattern="[0-9]{6}"
          maxlength="6"
          autocomplete="one-time-code"
          placeholder="000000"
          aria-label="Код комнаты"
        >
        <button type="submit" class="button button--primary button--wide" :disabled="!/^\d{6}$/.test(code)">
          Подключиться
        </button>
      </form>
      <small>Код показан рядом с QR-кодом на первом слайде.</small>
    </section>

    <template v-else>
      <section
        class="remote-preview"
        @touchstart.passive="onTouchStart"
        @touchend.passive="onTouchEnd"
      >
        <div class="remote-preview__current">
          <span>Сейчас на экране</span>
          <SlidePreview
            :src="deck.assets.slides[current] ?? null"
            :slide="currentSlide"
            :aspect="deck.doc.aspect"
            :alt="`Текущий слайд ${current + 1}`"
            eager
          />
        </div>
        <div v-if="nextSlide" class="remote-preview__next">
          <span>Дальше</span>
          <SlidePreview
            :src="deck.assets.slides[current + 1] ?? null"
            :slide="nextSlide"
            :aspect="deck.doc.aspect"
            alt="Следующий слайд"
          />
        </div>
        <div v-else class="remote-preview__next remote-preview__finish">
          <span>Дальше</span>
          <strong>Финал</strong>
          <small>Это последний слайд</small>
        </div>
      </section>

      <section class="remote-meta">
        <div class="remote-slide-number">
          <span>Слайд</span>
          <strong>{{ current + 1 }}<small>/{{ deck.doc.slides.length }}</small></strong>
        </div>
        <div class="remote-timer">
          <span>Время выступления</span>
          <div>
            <strong>{{ timerText }}</strong>
            <button type="button" class="timer-button" @click="toggleTimer">
              {{ timerRunning ? 'Пауза' : 'Продолжить' }}
            </button>
            <button type="button" class="timer-button" @click="resetTimer">Сброс</button>
          </div>
        </div>
      </section>

      <section class="speaker-notes">
        <span class="eyebrow">Заметки докладчика</span>
        <p v-if="currentNotes">{{ currentNotes }}</p>
        <p v-else class="speaker-notes__empty">
          Для этого слайда заметок нет. Можно говорить своими словами.
        </p>
      </section>

      <nav class="remote-controls" aria-label="Управление слайдами">
        <button
          type="button"
          :disabled="current === 0"
          aria-label="Предыдущий слайд"
          @click="go(current - 1)"
        >
          <span>←</span>
          <small>Назад</small>
        </button>
        <button
          type="button"
          class="remote-controls__next"
          :disabled="current === deck.doc.slides.length - 1"
          aria-label="Следующий слайд"
          @click="go(current + 1)"
        >
          <small>{{ current === deck.doc.slides.length - 1 ? 'Финал' : 'Дальше' }}</small>
          <span>→</span>
        </button>
      </nav>
    </template>
  </main>
</template>
